import { Link } from "react-router-dom";
import {
  Sparkle,
  ArrowRight,
  Compass,
  ChatCircleDots,
  MapTrifold,
  AirplaneTilt,
  PaperPlaneTilt,
  CurrencyInr,
} from "@phosphor-icons/react";
import { track } from "../lib/analytics";

// Bright tropical aerial — used as poster + mobile fallback
const HERO_IMG =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=80";

// Cinematic airplane-wing-over-clouds loop (Pexels CDN)
// HD 1280x720, ~5.4MB — sharp on every viewport, mobile-friendly bandwidth
const HERO_VIDEO =
  "https://videos.pexels.com/video-files/8429332/8429332-hd_1280_720_24fps.mp4";

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative w-full overflow-hidden sky-bg"
      style={{ minHeight: "min(92vh, 760px)" }}
    >
      {/* Always-rendered image (base layer + mobile + video poster fallback) */}
      <img
        src={HERO_IMG}
        alt="Aerial view of a turquoise ocean and sand"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Cinematic airplane wing loop — plays on every viewport (HD 720p, ~5.4MB, smooth on mobile + desktop) */}
      <video
        data-testid="hero-video"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "brightness(1.08) saturate(1.2) contrast(1.03)" }}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        poster={HERO_IMG}
        aria-hidden="true"
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>

      {/* Soft cream → transparent → cream wash to keep airy + readable */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(247,243,234,0.25) 0%, rgba(247,243,234,0.05) 35%, rgba(247,243,234,0.65) 80%, #F7F3EA 100%)",
        }}
      />

      {/* Sky tint on the right */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-soft-light"
        style={{
          background:
            "radial-gradient(ellipse at right top, rgba(95,179,227,0.35), transparent 55%)",
        }}
      />

      {/* Decorative floating airplane (top-right) */}
      <div className="absolute top-28 right-6 sm:right-16 z-10 hidden sm:flex items-center gap-3 px-4 py-2 rounded-full bg-white/70 backdrop-blur-md border border-white/80 shadow-sm">
        <span
          className="w-7 h-7 rounded-full grid place-items-center"
          style={{ background: "#FF7E4D" }}
        >
          <AirplaneTilt size={14} color="#fff" weight="fill" />
        </span>
        <span className="text-xs font-medium text-slate-700">
          Now boarding — 12 destinations
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-16 sm:pt-44 sm:pb-32 flex flex-col items-start">
        <span
          data-testid="hero-kicker"
          className="kicker inline-flex items-center gap-2"
        >
          <Sparkle size={14} weight="fill" /> Free AI Travel Planner · Made for Indian travelers
        </span>

        <h1
          data-testid="hero-title"
          className="font-serif text-[44px] leading-[1.02] sm:text-7xl lg:text-[110px] sm:leading-[0.95] tracking-tight mt-5 sm:mt-6 max-w-5xl text-slate-900"
        >
          The free AI planner
          <br />
          <span style={{ color: "#2A7FB8" }} className="italic">
            for your next escape.
          </span>
        </h1>

        {/* Visually-hidden but SEO-loaded keyword paragraph */}
        <p className="sr-only">
          Flycher is a free AI travel planner, AI trip planner and AI itinerary generator built
          for Indian travelers. Generate day-by-day AI itineraries, check visa rules for Indian
          passport holders, calculate trip budgets in INR, compare the best forex cards (Niyo,
          Wise, IndusInd), and learn 30 essential travel phrases per country — all completely
          free, no signup required. The AI trip planner supports Vietnam, Thailand, Bali, Japan,
          Maldives, Singapore, Malaysia, Sri Lanka, Nepal, Philippines, Vietnam and more.
        </p>

        <div
          data-testid="hero-tagline"
          className="mt-5 sm:mt-6 inline-flex max-w-2xl items-start gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl bg-white/85 backdrop-blur-md border border-white/70 shadow-md shadow-sky-900/10"
        >
          <span
            className="inline-flex w-6 h-6 mt-0.5 rounded-full grid place-items-center flex-shrink-0"
            style={{ background: "#3DB68B" }}
          >
            <CurrencyInr size={14} color="#ffffff" weight="bold" />
          </span>
          <p className="font-serif italic text-[15px] sm:text-xl lg:text-2xl leading-snug">
            <span style={{ color: "#2A7FB8" }} className="font-semibold">
              Get flight &amp; hotel deals
            </span>{" "}
            <span className="text-slate-800">that </span>
            <span style={{ color: "#FF7E4D" }} className="font-semibold">
              save thousands
            </span>
            <span className="text-slate-800"> of your money.</span>
          </p>
        </div>

        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Link
            to="/itinerary"
            data-testid="hero-cta-itinerary"
            onClick={() => track("hero_cta_clicked", { target: "itinerary" })}
            className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:py-3 rounded-full text-sm font-semibold shadow-lg shadow-orange-200/60"
            style={{ background: "#FF7E4D", color: "#ffffff" }}
          >
            Generate my budget itinerary
            <ArrowRight
              size={16}
              weight="bold"
              className="transition-transform group-hover:translate-x-1"
            />
          </Link>
          <Link
            to="/experts"
            data-testid="hero-cta-experts"
            onClick={() => track("hero_cta_clicked", { target: "experts" })}
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:py-3 rounded-full text-sm font-medium border border-slate-300 bg-white/80 backdrop-blur-md text-slate-800 hover:bg-white"
          >
            <ChatCircleDots size={16} weight="duotone" /> Chat with an expert
          </Link>
        </div>

        <div className="mt-12 sm:mt-16 grid grid-cols-3 gap-3 sm:gap-8 max-w-2xl">
          {[
            {
              icon: Compass,
              label: "12 countries",
              sub: "Curated for India",
            },
            {
              icon: PaperPlaneTilt,
              label: "AI experts",
              sub: "Visa, flights, hacks",
            },
            { icon: MapTrifold, label: "PDF trip-board", sub: "Day by day" },
          ].map((it, i) => (
            <div key={i} className="flex flex-col gap-2">
              <span
                className="w-9 h-9 rounded-full grid place-items-center"
                style={{ background: "rgba(42,127,184,0.12)" }}
              >
                <it.icon size={18} weight="duotone" color="#2A7FB8" />
              </span>
              <span className="text-sm font-semibold text-slate-900">
                {it.label}
              </span>
              <span className="text-[11px] uppercase tracking-widest text-slate-500">
                {it.sub}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom feather into next section */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, transparent, #F7F3EA)",
        }}
      />
    </section>
  );
};
